import { FC, useEffect, useState } from "react";

import { Product, ProductVariant, WeightUnit } from "../types/api";
import { DataGrid, GridColDef, GridRowModel } from "@mui/x-data-grid";
import { IconButton, MenuItem, Paper, Select, Tooltip, Typography } from "@mui/material";
import { currencyFormatter } from "src/util";
import { ViewVariantStockModal } from "./modal.view-variantstock";
import { ImageViewerModal } from "./modal.image-viewer";
import { dataTables } from "src/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import PoundOffIcon from "./icons/pound-off.icon";
import { endSaleOnVariant, isVariantOnSale, syncSaleTag } from "src/utils/sale-utils";
import { padding } from "@mui/system";
import { sanitiseFilterValue } from "src/utils/variant-utils";

interface Props {
  parentProduct: Product;
  setParentProduct?: (p: Product) => void;
  editMode?: boolean;
}

const ProductVariantTable: FC<Props> = (props: Props) => {
  const editMode = props.editMode === true;
  const [rows, setRows] = useState<any[]>([]);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState({ url: "", alt: "" });

  const handleImageClick = (imageUrl: string, imageAlt: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click
    setSelectedImage({ url: imageUrl, alt: imageAlt });
    setImageModalOpen(true);
  };

  const handleCloseModal = () => {
    setImageModalOpen(false);
  };

  useEffect(() => {
    const newRows = props.parentProduct?.variants
      ?.filter((pv) => pv["pendingDelete"] !== true)
      .map((pv: ProductVariant) => {
        const row = {
          id: pv.guid || `${props.parentProduct.guid}-${pv.sku}`,
          guid: pv.guid,
          image: (() => {
            const imageUrl = pv.imageUrls?.[0];
            return imageUrl ? [imageUrl, pv.name] : undefined;
          })(),
          sku: pv.sku,
          supplierCode: pv.supplierCode,
          barcode: pv.barcode,
          retailPrice: currencyFormatter.format(pv.retailPrice / 100),
          previousPrice: currencyFormatter.format((pv.previousPrice || 0) / 100),
          onSale: isVariantOnSale(pv),
          buyPrice: currencyFormatter.format(pv.buyPrice / 100),
          packSize: pv.boughtInPacks ? pv.packSize : 1,
          available: pv.stockOnHand,
          location: props.parentProduct.location,
          weight: pv.weight || 0,
          weightUnit: pv.weightUnit,
          notifyAdminOnSale: pv.notifyAdminOnSale || false,
        };

        if (filterNames) {
          const filterValues: string[] = pv.filterValues 
            ? (Array.isArray(pv.filterValues) 
                ? pv.filterValues 
                : (pv.filterValues as string).split(","))
            : [];
          let filterIdx: number;

          for (filterIdx = 0; filterIdx < filterNames.length; filterIdx++) {
            row[filterNames[filterIdx]] = filterValues[filterIdx];
          }
        }

        return row;
      });

    setRows(newRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.parentProduct]);

  // Check if this is a new product (no guid/id) or has temporary SKUs
  const isNewProduct = !props.parentProduct?.guid || !props.parentProduct?.id;
  const hasTemporarySkus = props.parentProduct?.variants?.some(v => v.sku?.startsWith('temp-'));
  const shouldHideSku = editMode && (isNewProduct || hasTemporarySkus);

  const columns: GridColDef[] = [
    { field: "id" },
    { field: "guid" },
    {
      field: "image",
      headerName: "Image",
      sortable: false,
      minWidth: 80,
      align: "center",
      renderCell: (params) => {
        if (params.value) {
          return (
            <img
              src={params.value[0]}
              height={40}
              width={40}
              alt={params.value[1]}
              onClick={(e) => handleImageClick(params.value[0], params.value[1], e)}
              style={{ cursor: "pointer" }}
              onError={(e) => {
                (e.target as HTMLImageElement).src = "https://upload.wikimedia.org/wikipedia/commons/1/14/No_Image_Available.jpg";
              }}
            />
          );
        } else {
          return <Typography variant="caption">No image</Typography>;
        }
      },
    },
  ];

  // Add SKU column conditionally - hide for new products with temp SKUs
  if (!shouldHideSku) {
    columns.push({
      field: "sku",
      headerName: "SKU",
      minWidth: 150,
      flex: 1,
      editable: editMode,
    });
  }

  // Add remaining columns
  columns.push(
    {
      field: "supplierCode",
      headerName: "Supplier Code",
      minWidth: 150,
      flex: 1,
      editable: editMode,
    },
    {
      field: "barcode",
      headerName: "Barcode",
      minWidth: 150,
      flex: 1,
      editable: editMode,
    },
    {
      field: "retailPrice",
      headerName: "Retail £",
      minWidth: 100,
      flex: 1,
      align: "right",
      editable: editMode,
      renderCell: (params) => (
        <Typography
          variant="body2"
          component="span"
          color={
            +params.value.replaceAll(/[^0-9]/g, "") <
            +params.row.previousPrice.replaceAll(/[^0-9]/g, "")
              ? "error"
              : "default"
          }
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "previousPrice",
      headerName: "Previous £",
      minWidth: 100,
      flex: 1,
      align: "right",
      editable: editMode,
    },
    {
      field: "buyPrice",
      headerName: "Buy £",
      minWidth: 100,
      flex: 1,
      align: "right",
      editable: editMode,
    },
    {
      field: "packSize",
      type: "number",
      headerName: "Pack Size",
      minWidth: 100,
      flex: 1,
      align: "right",
      editable: editMode,
    },
  );

  // Add Notify Admin checkbox column (in edit mode only)
  if (editMode) {
    columns.push({
      field: "notifyAdminOnSale",
      headerName: "Notify on Sale",
      minWidth: 120,
      type: "boolean",
      flex: 1,
      editable: true,
      align: "center",
    });
  }

  const filterNames: string[] = props.parentProduct?.filterFields
    ? (Array.isArray(props.parentProduct.filterFields)
        ? props.parentProduct.filterFields
        : (props.parentProduct.filterFields as string).split(","))
    : [];

  // Work out which rows have a duplicate combination of filter values so their
  // filter cells can be highlighted in red. Each variant must have a unique
  // combination of option values or the product cannot be published to Shopify.
  const _rowFilterKey = (row: any): string =>
    filterNames
      .map((f) => (row?.[f] ?? "").toString().trim().toLowerCase())
      .join("\0");

  const _duplicateRowIds = (() => {
    const counts = new Map<string, number>();
    (rows || []).forEach((r) => {
      const key = _rowFilterKey(r);
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    const ids = new Set<any>();
    (rows || []).forEach((r) => {
      if ((counts.get(_rowFilterKey(r)) || 0) > 1) {
        ids.add(r.id);
      }
    });
    return ids;
  })();

  if (filterNames) {
    filterNames.forEach((f: string) => {
      if (f !== "") {
        columns.push({
          field: f,
          headerName: f,
          minWidth: 100,
          flex: 1,
          align: "left",
          editable: editMode,
          cellClassName: (params) =>
            _duplicateRowIds.has(params.id) ? "cell-duplicate" : "",
        });
      }
    });
  }

  columns.push({
    field: "weight",
    headerName: "Weight",
    type: "number",
    minWidth: 75,
    flex: 1,
    editable: editMode,
  });

  columns.push({
    field: "weightUnit",
    headerName: "Unit",
    minWidth: 60,
    flex: 1,
    editable: editMode,
    renderCell: (params) => (
      <>
        {editMode && (
          <Select
            value={params.row.weightUnit}
            onChange={(e) => _handleEditWeightUnit(params.row.sku, e)}
            sx={{
              minHeight: 32,
              fontSize: '0.875rem',
              '& .MuiSelect-select': {
                padding: '4px 8px',
                minHeight: 'auto'
              }
            }}
          >
            <MenuItem key={WeightUnit.G} value="g">
              <Typography variant="caption">g</Typography>
            </MenuItem>
            <MenuItem key={WeightUnit.KG} value="Kg">
              <Typography variant="caption">Kg</Typography>
            </MenuItem>
          </Select>
        )}
        {!editMode && (
          <Typography variant="caption">{params.row.weightUnit}</Typography>
        )}
      </>
    ),
  });

  // Don't show these if we are editing the variants.
  if (!editMode) {
    columns.push({
      field: "available",
      headerName: "On Hand",
      align: "center",
      minWidth: 90,
      flex: 1,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      editable: false,
    });

    columns.push({
      field: "view",
      headerName: "Activity",
      disableColumnMenu: true,
      sortable: false,
      filterable: false,
      minWidth: 100,
      flex: 1,
      align: "center",
      renderCell: (params) => (
        <ViewVariantStockModal color="inherit" sx={{p: 0}} variant={params.row} />
      ),
      editable: false,
    });
  }

  // Only show if we ARE editing the variants.
  if (editMode) {
    columns.push({
      field: "actions",
      headerName: "Actions",
      disableColumnMenu: true,
      sortable: false,
      filterable: false,
      flex: 1,
      // Right-aligned so the Delete icon sits in the same place on every row
      // whether or not the End sale icon is present beside it.
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <>
          {params.row.onSale && (
            <Tooltip title={`End sale: restore retail price to ${params.row.previousPrice}`}>
              <IconButton
                onClick={(e) => _endSale(e, params.row)}
                aria-label="End sale"
                data-testid={`end-sale-${params.row.sku}`}
                sx={{ padding: '4px', minHeight: 32, minWidth: 32 }}
              >
                <PoundOffIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Delete variant">
            <span>
              <IconButton
                disabled={props.parentProduct.variants.length === 1}
                onClick={(e) => _deleteRow(e, params.row)}
                aria-label="Delete variant"
                sx={{ padding: '4px', minHeight: 32, minWidth: 32 }}
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        </>
      ),
    });
  }

  // Called when exiting an edit state
  const _handleEdits = (
    newRow: GridRowModel,
    oldRow: GridRowModel
  ): GridRowModel => {
    const v: ProductVariant = props.parentProduct.variants.filter(
      (i) => i.sku === oldRow.sku // use the old row in case it's the SKU that has changed.
    )[0];

    // Check we are not trying to set a duplicate sku, as this will mess with this routine next time around
    if (
      newRow.sku !== oldRow.sku &&
      props.parentProduct.variants.filter((v: any) => v.sku === newRow.sku).length >
        0
    ) {
      alert("That SKU is already in use in this product!");
      newRow.sku = oldRow.sku;
    } else {
      v.sku = newRow.sku;
    }

    v.supplierCode = newRow.supplierCode;
    v.barcode = newRow.barcode;
    v.retailPrice = +newRow.retailPrice.replaceAll(/[^0-9]/g, "");
    v.previousPrice = +newRow.previousPrice.replaceAll(/[^0-9]/g, "");
    v.buyPrice = +newRow.buyPrice.replaceAll(/[^0-9]/g, "");
    v.packSize = +newRow.packSize;
    v.boughtInPacks = newRow.packSize > 1;
    // Sanitise each option value (commas removed, whitespace trimmed) and write
    // the cleaned values back to the row so the grid shows what will be saved.
    v.filterValues = _getFilterValues(newRow, props.parentProduct);
    filterNames.forEach((f, i) => {
      if (f !== "") newRow[f] = v.filterValues[i];
    });
    v.weight = +newRow.weight;
    v.notifyAdminOnSale = newRow.notifyAdminOnSale || false;

    // Keep the sale tag in step with ALL variants, not just the edited one:
    // taking one variant off sale must not strip the tag while a sibling is
    // still on sale.
    syncSaleTag(props.parentProduct);

    props.setParentProduct(props.parentProduct);

    // Finally commit the changes to the current view
    return { ...newRow, onSale: isVariantOnSale(v) };
  };

  // End the sale on one variant: retail price back to the previous price,
  // previous price cleared, sale tag recomputed across the product. A local
  // edit like any other on this page - nothing is saved until Save is pressed.
  const _endSale = (e: React.MouseEvent, row: any) => {
    e.stopPropagation();
    const v = props.parentProduct.variants.find((i: ProductVariant) =>
      row.guid ? i.guid === row.guid : i.sku === row.sku
    );
    if (!v) return;
    endSaleOnVariant(v);
    syncSaleTag(props.parentProduct);
    props.setParentProduct(props.parentProduct);
  };

  // A special case, as we manage this dropdown ourselves
  const _handleEditWeightUnit = (sku: string, e) => {
    const v = e.target.value;
    props.parentProduct.variants.filter((v: any) => v.sku === sku)[0].weightUnit = v;
    props.setParentProduct(props.parentProduct);
  };

  const _getFilterValues = (row: any, p: Product): string[] => {
    if (!p.filterFields) return [];
    
    const fields = p.filterFields 
      ? (Array.isArray(p.filterFields) 
          ? p.filterFields 
          : (p.filterFields as string).split(/\s*,\s*/))
      : [];
    
    return fields.map((key: string) => sanitiseFilterValue(row[key]));
  };

  const _deleteRow = (e, row) => {
    const d = props.parentProduct.variants.find((i) => i.sku === row.sku);

    // If this was already there flag it for deletion, but if we only just added it (without saving) just remove it
    if (d) {
      if (d.id > 0) {
        d["pendingDelete"] = true;
      } else {
        console.log(d);
        props.parentProduct.variants.splice(
          props.parentProduct.variants.indexOf(d),
          1
        );
      }
      props.setParentProduct(props.parentProduct);
    }
  };

  return (
    <>
      <Paper
        component="form"
        sx={{ p: "0px 4px", display: "flex", alignItems: "center" }}
      >
        {rows && (
          <DataGrid
            rows={rows}
            columns={columns}
            autoHeight
            density="compact"
            hideFooter={true}
            processRowUpdate={_handleEdits}
            onProcessRowUpdateError={(e) => alert(e)}
            disableRowSelectionOnClick
            columnVisibilityModel={{
              id: false,
              guid: false,
            }}
            sx={{
              ...dataTables,
              "& .cell-duplicate": {
                color: "error.main",
                fontWeight: 600,
              },
            }}
          />
        )}
      </Paper>
      <ImageViewerModal
        open={imageModalOpen}
        imageUrl={selectedImage.url}
        imageAlt={selectedImage.alt}
        onClose={handleCloseModal}
      />
    </>
  );
};

export default ProductVariantTable;
